/**
 * Floating contact widget (templates/components/layout/floating_chatbot.html).
 *
 * Posts to ContactUsCreateAPIView. Three things that version one got wrong and
 * that this file exists to keep right:
 *
 *   1. The endpoint is /api/contacts/create/ and the phone field is
 *      `phone_number`. The old widget posted to /api/contact-us/create/ — a 404 —
 *      so every message a visitor typed into it was lost.
 *   2. That view requires `form_timestamp` (stamped on load, must be >= 3s old)
 *      and rejects a filled `website` honeypot. Omit either and the answer is a
 *      400, whatever the visitor typed.
 *   3. Closing the panel must not wipe the draft. The old one called form.reset()
 *      on every close, including the close fired by an outside click, so a stray
 *      click on the page threw the message away. Here the draft is kept in
 *      sessionStorage and restored on the next open.
 *
 * Errors are read out of the API envelope ({status, status_code, message, data})
 * so a field error reaches the person who can fix it.
 */
(function () {
    'use strict';

    var STORAGE_DRAFT = 'floatingContactDraft';
    var STORAGE_SEEN = 'floatingContactSeen';
    var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled])';

    function init() {
        var root = document.getElementById('floating-contact');
        if (!root) return;

        var panel = document.getElementById('floating-contact-panel');
        var toggle = document.getElementById('floating-contact-toggle');
        var closeBtn = document.getElementById('floating-contact-close');
        var doneBtn = document.getElementById('floating-contact-done');
        var form = document.getElementById('floating-contact-form');
        var successPane = document.getElementById('floating-contact-success');
        var submitBtn = document.getElementById('floating-contact-submit');
        var submitLabel = submitBtn.querySelector('[data-submit-label]');
        var submitSpinner = submitBtn.querySelector('[data-submit-spinner]');
        var formError = document.getElementById('floating-contact-error');
        var iconOpen = toggle.querySelector('[data-icon-open]');
        var iconClose = toggle.querySelector('[data-icon-close]');
        var attention = toggle.querySelector('[data-attention]');
        var counter = document.getElementById('floating-contact-count');
        // Shares the right rail with this widget; the open panel covers its slot.
        var callButton = document.getElementById('floating-call-button');
        var whatsappButton = document.getElementById('floating-whatsapp-button');

        var fields = {
            name: document.getElementById('floating-contact-name'),
            email: document.getElementById('floating-contact-email'),
            phone_number: document.getElementById('floating-contact-phone'),
            message: document.getElementById('floating-contact-message')
        };
        var honeypot = document.getElementById('floating-contact-website');
        var timestamp = document.getElementById('floating-contact-timestamp');

        var isOpen = false;
        var isSending = false;

        /* ---------------------------------------------------------------- state */

        // Stamped on load, not on submit: the server measures the gap between the
        // two and treats anything under three seconds as a bot.
        if (timestamp) timestamp.value = Date.now().toString();

        function safeStorage(fn, fallback) {
            try { return fn(); } catch (err) { return fallback; }
        }

        function saveDraft() {
            var draft = {};
            Object.keys(fields).forEach(function (key) { draft[key] = fields[key].value; });
            safeStorage(function () {
                window.sessionStorage.setItem(STORAGE_DRAFT, JSON.stringify(draft));
            });
        }

        function restoreDraft() {
            var raw = safeStorage(function () {
                return window.sessionStorage.getItem(STORAGE_DRAFT);
            }, null);
            if (!raw) return;
            var draft;
            try { draft = JSON.parse(raw); } catch (err) { return; }
            Object.keys(fields).forEach(function (key) {
                if (typeof draft[key] === 'string') fields[key].value = draft[key];
            });
            updateCounter();
        }

        function clearDraft() {
            safeStorage(function () { window.sessionStorage.removeItem(STORAGE_DRAFT); });
        }

        /* ----------------------------------------------------------- validation */

        function setFieldError(key, text) {
            var field = fields[key];
            if (!field) return;
            var slot = document.getElementById(field.getAttribute('id') + '-error');
            if (text) {
                field.setAttribute('aria-invalid', 'true');
                if (slot) { slot.textContent = text; slot.classList.remove('hidden'); }
            } else {
                field.removeAttribute('aria-invalid');
                if (slot) { slot.textContent = ''; slot.classList.add('hidden'); }
            }
        }

        function clearErrors() {
            Object.keys(fields).forEach(function (key) { setFieldError(key, ''); });
            formError.textContent = '';
            formError.classList.add('hidden');
        }

        function validate() {
            clearErrors();
            var firstBad = null;

            if (!fields.name.value.trim()) {
                setFieldError('name', 'Please tell us your name.');
                firstBad = firstBad || fields.name;
            }

            var email = fields.email.value.trim();
            if (!email) {
                setFieldError('email', 'We need an email to reply to.');
                firstBad = firstBad || fields.email;
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setFieldError('email', 'That email address does not look right.');
                firstBad = firstBad || fields.email;
            }

            // Optional, so only a filled-in phone is checked. The old widget styled
            // the field green the moment the panel opened, because an empty
            // non-required input is :valid.
            var phone = fields.phone_number.value.replace(/[^\d]/g, '');
            if (fields.phone_number.value.trim() && phone.length < 8) {
                setFieldError('phone_number', 'Please enter a full phone number, or leave it blank.');
                firstBad = firstBad || fields.phone_number;
            }

            if (fields.message.value.trim().length < 10) {
                setFieldError('message', 'A sentence or two about the job helps us quote it.');
                firstBad = firstBad || fields.message;
            }

            if (firstBad) firstBad.focus();
            return !firstBad;
        }

        function updateCounter() {
            if (!counter) return;
            counter.textContent = fields.message.value.length + '/1000';
        }

        /* ----------------------------------------------------------- open/close */

        function focusable() {
            return Array.prototype.filter.call(
                panel.querySelectorAll(FOCUSABLE),
                function (el) { return el.offsetParent !== null; }
            );
        }

        function setCallButtonHidden(hidden) {
            [callButton, whatsappButton].forEach(function (btn) {
                if (!btn) return;
                btn.classList.toggle('opacity-0', hidden);
                btn.classList.toggle('pointer-events-none', hidden);
                btn.setAttribute('aria-hidden', hidden ? 'true' : 'false');
                var link = btn.querySelector('a');
                if (link) {
                    if (hidden) link.setAttribute('tabindex', '-1');
                    else link.removeAttribute('tabindex');
                }
            });
        }

        function open() {
            if (isOpen) return;
            isOpen = true;
            panel.classList.remove('opacity-0', 'scale-95', 'translate-y-2', 'pointer-events-none');
            panel.setAttribute('aria-hidden', 'false');
            toggle.setAttribute('aria-expanded', 'true');
            toggle.setAttribute('aria-label', root.classList.contains('is-chat-ready') ? 'Close chat' : 'Close contact form');
            iconOpen.classList.add('hidden');
            iconClose.classList.remove('hidden');
            setCallButtonHidden(true);

            if (attention) {
                attention.remove();
                attention = null;
                safeStorage(function () { window.localStorage.setItem(STORAGE_SEEN, '1'); });
            }

            // Only steal focus on a pointer-sized screen; on a phone this pops the
            // keyboard over the panel the moment it opens. Skipped while the success
            // pane is showing — its own Close button already holds focus.
            if (window.matchMedia('(min-width: 640px)').matches && successPane.classList.contains('hidden')) {
                fields.name.focus({ preventScroll: true });
            }

            if (window.trackEvent) window.trackEvent('floating_contact_opened', {});
        }

        function close() {
            if (!isOpen) return;
            isOpen = false;
            panel.classList.add('opacity-0', 'scale-95', 'translate-y-2', 'pointer-events-none');
            panel.setAttribute('aria-hidden', 'true');
            toggle.setAttribute('aria-expanded', 'false');
            toggle.setAttribute('aria-label', root.classList.contains('is-chat-ready') ? 'Chat with us' : 'Open contact form');
            iconOpen.classList.remove('hidden');
            iconClose.classList.add('hidden');
            setCallButtonHidden(false);
            // Draft survives the close deliberately — see the header comment.
            saveDraft();
            toggle.focus({ preventScroll: true });
        }

        /* --------------------------------------------------------------- submit */

        function setSending(sending) {
            isSending = sending;
            submitBtn.disabled = sending;
            submitLabel.textContent = sending ? 'Sending…' : 'Send message';
            submitSpinner.classList.toggle('hidden', !sending);
        }

        function showFormError(text) {
            formError.textContent = text;
            formError.classList.remove('hidden');
        }

        /** Surface whatever the envelope says: field errors first, then the message. */
        function applyServerErrors(payload) {
            var errors = (payload && payload.data && payload.data.errors) || (payload && payload.errors);
            var handled = false;

            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(function (key) {
                    var text = Array.isArray(errors[key]) ? errors[key][0] : String(errors[key]);
                    if (fields[key]) {
                        setFieldError(key, text);
                        handled = true;
                    }
                });
            }

            if (!handled) {
                showFormError((payload && payload.message) || 'We could not send that. Please try again, or call us.');
            }
        }

        function showSuccess() {
            form.classList.add('hidden');
            successPane.classList.remove('hidden');
            clearDraft();
            form.reset();
            updateCounter();
            // Ready for a second message if the panel is reopened.
            if (timestamp) timestamp.value = Date.now().toString();
            if (doneBtn) doneBtn.focus({ preventScroll: true });
        }

        function resetToForm() {
            successPane.classList.add('hidden');
            form.classList.remove('hidden');
            clearErrors();
        }

        function trackSubmission() {
            var email = fields.email.value.trim();
            try {
                if (window.identifyUser && email) {
                    window.identifyUser(email, {
                        email: email,
                        name: fields.name.value.trim(),
                        phone: fields.phone_number.value.trim()
                    });
                }
                if (window.trackEvent) {
                    window.trackEvent('contact_form_submitted', {
                        form_id: 'floating_contact',
                        has_phone: !!fields.phone_number.value.trim()
                    });
                }
            } catch (err) {
                if (window.console && console.warn) console.warn('floating contact analytics failed:', err);
            }
        }

        form.addEventListener('submit', function (event) {
            event.preventDefault();
            if (isSending) return;
            if (!validate()) return;

            setSending(true);

            var body = {
                name: fields.name.value.trim(),
                email: fields.email.value.trim(),
                phone_number: fields.phone_number.value.trim(),
                message: fields.message.value.trim(),
                website: honeypot ? honeypot.value : '',
                form_timestamp: timestamp ? timestamp.value : '',
                source: 'floating_widget',
                marketing_attribution: window.getMarketingAttribution ? window.getMarketingAttribution() : null
            };

            fetch(root.getAttribute('data-endpoint'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            }).then(function (response) {
                return response.json().catch(function () { return null; }).then(function (payload) {
                    return { ok: response.ok, payload: payload };
                });
            }).then(function (result) {
                if (result.ok) {
                    trackSubmission();
                    showSuccess();
                } else {
                    applyServerErrors(result.payload);
                }
            }).catch(function (err) {
                if (window.console && console.error) console.error('floating contact submit failed:', err);
                showFormError('We could not reach the server. Please check your connection and try again.');
            }).finally(function () {
                setSending(false);
            });
        });

        /* --------------------------------------------------------------- wiring */

        toggle.addEventListener('click', function () {
            if (isOpen) { close(); } else { open(); }
        });
        closeBtn.addEventListener('click', close);
        if (doneBtn) doneBtn.addEventListener('click', function () {
            close();
            resetToForm();
        });

        Object.keys(fields).forEach(function (key) {
            fields[key].addEventListener('input', function () {
                setFieldError(key, '');
                if (key === 'message') updateCounter();
                saveDraft();
            });
        });

        // Outside click closes the panel but keeps the draft. A click inside the
        // panel — including on a native date/select popup — must not.
        var PICKER = '.flatpickr-calendar, .fp-backdrop';
        // Where the press began. A click that started inside the panel or the date
        // picker and was released elsewhere (the picker's backdrop goes away under
        // the pointer, so the click lands on body) is not an outside click.
        var pressStartedInside = false;
        document.addEventListener('pointerdown', function (event) {
            var target = event.target;
            pressStartedInside = !!(target && (root.contains(target) || (target.closest && (target.closest(PICKER) || target.closest('#mobile-bar-chat-btn')))));
        }, true);

        document.addEventListener('click', function (event) {
            if (!isOpen || isSending) return;
            if (root.contains(event.target)) return;
            // A chat chip or card button removes itself as it is clicked, so by the
            // time this runs it is no longer in the document. That click came from
            // inside the panel; it is not an outside click.
            if (!event.target.isConnected) return;
            // The shared date picker (datetime-pickers.js) opens on body with a
            // full-screen backdrop; clicks on it belong to the picker.
            if (event.target.closest && (event.target.closest(PICKER) || event.target.closest('#mobile-bar-chat-btn'))) return;
            if (document.querySelector('.flatpickr-calendar.open')) return;
            if (pressStartedInside) return;
            close();
        });

        document.addEventListener('keydown', function (event) {
            if (!isOpen) return;

            if (event.key === 'Escape') {
                close();
                return;
            }

            // Keep Tab inside the panel while it is open; it is a dialog on top of
            // the page, and tabbing out of it silently leaves an open form behind.
            if (event.key === 'Tab') {
                var items = focusable();
                if (!items.length) return;
                var first = items[0];
                var last = items[items.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        });

        // Phone: group as 0400 000 000 while typing, matching the contact page.
        fields.phone_number.addEventListener('input', function (event) {
            var digits = event.target.value.replace(/\D/g, '').slice(0, 10);
            if (digits.length > 7) {
                event.target.value = digits.slice(0, 4) + ' ' + digits.slice(4, 7) + ' ' + digits.slice(7);
            } else if (digits.length > 4) {
                event.target.value = digits.slice(0, 4) + ' ' + digits.slice(4);
            } else {
                event.target.value = digits;
            }
        });

        if (attention && safeStorage(function () { return window.localStorage.getItem(STORAGE_SEEN); }, null)) {
            attention.remove();
            attention = null;
        }

        restoreDraft();
        updateCounter();

        if (typeof window.initLucideIcons === 'function') window.initLucideIcons();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}());
